/**
 * Analytics Weekly Schemas
 *
 * @layer features
 * @slice analytics-weekly
 * @segment api
 */

import { z } from 'zod';

const nullableNumberFromMissingSchema = z.preprocess(
  (value) => (value === undefined ? null : value),
  z.number().nullable()
);

const nullableStringFromMissingSchema = z.preprocess(
  (value) => (value === undefined ? null : value),
  z.string().nullable()
);

export const analyticsWeeklyMetricSchema = z.object({
  total: z.number().nullable(),
  deltaPercent: z.number().nullable(),
});

export const analyticsWeeklyListItemSchema = z.object({
  id: z.string(),
  bubbleId: z.string(),
  weekStart: z.string().nullable(),
  weekEnd: z.string().nullable(),
  analyticsWeeklyTotalRegistrations: z.number().nullable(),
  analyticsWeeklyPaidRegistrations: z.number().nullable(),
  analyticsWeeklyUnpaidRegistrations: z.number().nullable(),
  analyticsAvg3MonthRegistrations: z.number().nullable(),
  analyticsWeeklyRegistrationsChangeVs3mAvgPct: z.number().nullable(),
  analyticsWeeklyPaidRegistrationsToTotalPct: z.number().nullable(),
  analyticsWeeklyUnpaidRegistrationsToTotalPct: z.number().nullable(),
  analyticsWeeklyTotalTrainingProgramsSold: z.number().nullable(),
  analyticsWeeklyTotalTrainingProgramsSoldShopify: z.number().nullable(),
  analyticsWeeklyTotalTrainingProgramsSoldIos: z.number().nullable(),
  analyticsWeeklyTotalTrainingProgramsSoldAndroid: z.number().nullable(),
  analyticsWeeklyTrainingProgramsSoldChangeVs3mAvgPct: z.number().nullable(),
  analyticsWeeklyActiveInstructors: z.number().nullable(),
  analyticsAvg3MonthActiveInstructors: z.number().nullable(),
  analyticsWeeklySchoolsActiveMembership: z.number().nullable(),
  analyticsWeeklyActiveInstructorsChangeVs3mAvgPct: z.number().nullable(),
  analyticsWeeklySchoolsActiveMembershipChangeVs3mAvgPct: z.number().nullable(),
  analyticsWeeklyPaidMemberships: z.number().nullable(),
  analyticsWeeklyExpiredMemberships: z.number().nullable(),
  analyticsWeeklyPaidMembershipsChangeVs3mAvgPct: z.number().nullable(),
  analyticsWeeklyEmModulesStarted: z.number().nullable(),
  analyticsWeeklyEmModulesInProgress: z.number().nullable(),
  analyticsWeeklyEmModulesCompleted: z.number().nullable(),
  analyticsWeeklyEmModulesStartedChangeVs3mAvgPct: z.number().nullable(),
  analyticsWeeklyEmModulesCompletedToStartedPct: z.number().nullable(),
  analyticsWeeklyEmModulesStartedToRegistrationsPct: z.number().nullable(),
  analyticsWeeklyEmExamsStarted: z.number().nullable(),
  analyticsWeeklyEmExamsInProgress: z.number().nullable(),
  analyticsWeeklyEmExamsCompleted: z.number().nullable(),
  analyticsWeeklyEmExamsStartedChangeVs3mAvgPct: z.number().nullable(),
  analyticsWeeklyEmExamsCompletedToStartedPct: z.number().nullable(),
  analyticsWeeklyEmExamsStartedToRegistrationsPct: z.number().nullable(),
  analyticsWeeklyHelpdeskTaskCreated: z.number().nullable(),
  analyticsWeeklyHelpdeskTaskClosed: z.number().nullable(),
  analyticsWeeklyHelpdeskTaskAvgHoursInWork: z.number().nullable(),
  analyticsWeeklyNewTicketsChangeVs3mAvgPct: z.number().nullable(),
  analyticsWeeklyAvgResponseTimeChangeVs3mAvgPct: z.number().nullable(),
  analyticsWeeklyTotalTaskCreated: z.number().nullable(),
  analyticsWeeklyTotalTaskClosed: z.number().nullable(),
  analyticsWeeklyTotalTaskAvgHoursInWork: z.number().nullable(),
  analyticsAvg3MonthTotalTrainingProgramsSold: z.number().nullable(),
  analyticsAvg3MonthHelpdeskTaskCreated: z.number().nullable(),
  analyticsAvg3MonthHelpdeskTaskAvgHoursInWork: z.number().nullable(),
  analyticsAvg3MonthSchoolsActiveMembership: z.number().nullable(),
  analyticsAvg3MonthEmExamsStarted: z.number().nullable(),
  analyticsAvg3MonthEmModulesStarted: z.number().nullable(),
  analyticsAvg3MonthPaidMemberships: z.number().nullable(),
  analyticsAvg3MonthRegistrationsSoldMolchanovsMovementSchool: z.number().nullable(),
  analyticsWeeklyTotalRegistrationsMolchanovsMovementSchool: z.number().nullable(),
  analyticsWeeklyRegistrationsSoldMolchanovsMovementSchool: z.number().nullable(),
  analyticsWeeklyRegistrationsSoldMovementSchoolTotalChangeVs3mAvgPct: z.number().nullable(),
  analyticsWeeklyRegistrationsCompletedMolchanovsMovementSchool: z.number().nullable(),
  analyticsWeeklyRegistrationsInProgressMolchanovsMovementSchool: z.number().nullable(),
  analyticsWeeklyRegistrationsSoldShopifyMolchanovsMovementSchool: z.number().nullable(),
  analyticsWeeklyRegistrationsSoldShopifyMovementSchoolToTotalPct: z.number().nullable(),
  analyticsWeeklyInstructorLedRegistrations: z.number().nullable(),
  analyticsWeeklyInstructorLedCertificationRegistrations: z.number().nullable(),
  analyticsWeeklyInstructorLedCertificationPercentageRegistrations: z.number().nullable(),
  analyticsWeeklyInstructorLedCCFRegistrations: z.number().nullable(),
  analyticsWeeklyInstructorLedCCFPercentageRegistrations: z.number().nullable(),
  analyticsWeeklyInstructorLedLiabilityReleaseRegistrations: z.number().nullable(),
  analyticsWeeklyInstructorLedLiabilityReleasePercentageRegistrations: z.number().nullable(),
  analyticsWeeklyInstructorLedMainExamRegistrations: z.number().nullable(),
  analyticsWeeklyInstructorLedMainExamPercentageRegistrations: z.number().nullable(),
  analyticsWeeklyInstructorLedMainModuleRegistrations: z.number().nullable(),
  analyticsWeeklyInstructorLedMainModulePercentageRegistrations: z.number().nullable(),
  analyticsWeeklyInstructorLedMedicalFormRegistrations: z.number().nullable(),
  analyticsWeeklyInstructorLedMedicalFormPercentageRegistrations: z.number().nullable(),
  analyticsNsm30dActiveFreedivers: nullableNumberFromMissingSchema,
  analyticsNsm30dGearActiveFreedivers: nullableNumberFromMissingSchema,
  analyticsNsm30dMoveActiveFreedivers: nullableNumberFromMissingSchema,
  analyticsDomain: z.string().nullable(),
  analyticsInsights: z.string().nullable().optional(),
  syncedAt: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  registrations: analyticsWeeklyMetricSchema,
  trainingProgramsSold: analyticsWeeklyMetricSchema,
  activeInstructors: analyticsWeeklyMetricSchema,
});

const analyticsWeeklyLegacyResponseSchema = z.object({
  items: z.array(analyticsWeeklyListItemSchema),
  page: z.number(),
  pageSize: z.number(),
  totalItems: z.number(),
  totalPages: z.number(),
  lastSyncAt: z.string().nullable(),
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asNullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function canonicalReportToLegacyListItem(value: unknown) {
  const report = isRecord(value) ? value : {};
  const metricsContainer = isRecord(report.metrics) ? report.metrics : {};
  const metrics = isRecord(metricsContainer.metrics) ? metricsContainer.metrics : {};
  const content = isRecord(report.content) ? report.content : {};
  const defaults = Object.fromEntries(
    Object.keys(analyticsWeeklyListItemSchema.shape).map((key) => [key, null])
  );
  const totalRegistrations = asNullableNumber(metrics.analyticsWeeklyTotalRegistrations);
  const activeInstructors = asNullableNumber(metrics.analyticsWeeklyActiveInstructors);

  return {
    ...defaults,
    ...metrics,
    id: typeof report.id === 'string' ? report.id : '',
    bubbleId: typeof report.id === 'string' ? report.id : '',
    weekStart: typeof report.weekStart === 'string' ? report.weekStart : null,
    weekEnd: typeof report.weekEnd === 'string' ? report.weekEnd : null,
    analyticsDomain: null,
    analyticsInsights: typeof content.generalInsights === 'string' ? content.generalInsights : null,
    syncedAt: null,
    createdAt: null,
    updatedAt: null,
    registrations: {
      total: totalRegistrations,
      deltaPercent: asNullableNumber(metrics.analyticsWeeklyRegistrationsChangeVs3mAvgPct),
    },
    trainingProgramsSold: {
      total: asNullableNumber(metrics.analyticsWeeklyTotalTrainingProgramsSold),
      deltaPercent: asNullableNumber(metrics.analyticsWeeklyTrainingProgramsSoldChangeVs3mAvgPct),
    },
    activeInstructors: {
      total: activeInstructors,
      deltaPercent: asNullableNumber(metrics.analyticsWeeklyActiveInstructorsChangeVs3mAvgPct),
    },
    analyticsWeeklyMainReport: typeof content.mainReport === 'string' ? content.mainReport : null,
    analyticsWeeklyCjmBottleneckInsight:
      typeof content.bottleneckInsights === 'string' ? content.bottleneckInsights : null,
    analyticsWeeklyInsights:
      typeof content.generalInsights === 'string' ? content.generalInsights : null,
  };
}

function normalizeCanonicalReportCollection(value: unknown) {
  if (!isRecord(value) || !isRecord(value.data) || !Array.isArray(value.data.items)) return value;
  const page = typeof value.data.page === 'number' ? value.data.page : 1;
  const pageSize =
    typeof value.data.pageSize === 'number' ? value.data.pageSize : value.data.items.length;
  const totalItems =
    typeof value.data.totalItems === 'number' ? value.data.totalItems : value.data.items.length;
  return {
    items: value.data.items.map(canonicalReportToLegacyListItem),
    page,
    pageSize,
    totalItems,
    totalPages:
      typeof value.data.totalPages === 'number'
        ? value.data.totalPages
        : Math.ceil(totalItems / pageSize),
    lastSyncAt: null,
  };
}

export const analyticsWeeklyResponseSchema = z.preprocess(
  normalizeCanonicalReportCollection,
  analyticsWeeklyLegacyResponseSchema
);

const openAIWeeklyReportDiagnosticsSummarySchema = z.preprocess(
  (value) => (value === undefined ? null : value),
  z
    .object({
      status: z.string().nullable(),
      model: z.string().nullable(),
      attempts: z.number().nullable(),
      retryCount: z.number().nullable(),
      contextBytes: z.number().nullable(),
      durationMs: z.number().nullable(),
      generatedAt: z.string().nullable(),
      completedAt: z.string().nullable(),
      responseIdPresent: z.boolean(),
      previousResponseIdPresent: z.boolean(),
    })
    .nullable()
);

const platformHealthPeriodSummarySchema = z.preprocess(
  (value) => (value === undefined ? null : value),
  z
    .object({
      start: z.string().nullable(),
      end: z.string().nullable(),
      rule: z.literal('[weekStart - 30 days, weekStart)'),
    })
    .nullable()
);

const canonicalDataQualitySchema = z.object({
  version: z.literal('weekly-analytics-quality.v1'),
  completeness: z.enum(['unconfirmed', 'unknown']),
  assessedAt: z.iso.datetime(),
  correlationId: z.string(),
  issues: z.array(
    z.object({
      metricKey: z.string().nullable(),
      source: z.string().nullable(),
      stage: z.enum(['source_eligibility', 'metric_calculation']),
      reasonCode: z.string(),
      occurredAt: z.iso.datetime(),
      retryable: z.boolean(),
    })
  ),
});

const historicalContentSchema = z.object({
  version: z.number().int().positive().nullable(),
  metricsVersion: z.number().int().positive().nullable(),
  basisStatus: z.enum(['stale', 'unknown']),
  mainReport: z.string().nullable(),
  bottleneckInsights: z.string().nullable(),
  generalInsights: z.string().nullable(),
});

export const analyticsWeeklyDetailItemSchema = analyticsWeeklyListItemSchema.extend({
  analyticsInstructorsMore1CourseInLast90Days: z.number().nullable(),
  analyticsSchoolsMore1CourseInLast90Days: z.number().nullable(),
  analyticsWeeklyInstructorsTrainersMore1CourseInLast90Days: z.number().nullable(),
  analyticsWeeklyInstructorsTrainersMore1CourseInLast365Days: nullableNumberFromMissingSchema,
  analyticsWeeklyCertificatesNumber: z.number().nullable(),
  analyticsWeeklyInstructorSubscriptionsRevenue: z.number().nullable(),
  analyticsWeeklyRegistrationsRevenue: z.number().nullable(),
  analyticsWeeklyTrainingProgramsRevenue: z.number().nullable(),
  analyticsAvg3MonthPaidRegistrations: z.number().nullable(),
  analyticsWeeklyPaidRegistrationsChangeVs3mAvgPct: z.number().nullable(),
  analyticsWeeklyTrainingProgramsSoldAndroidToTotalPct: z.number().nullable(),
  analyticsWeeklyTrainingProgramsSoldIosToTotalPct: z.number().nullable(),
  analyticsWeeklyTrainingProgramsSoldShopifyToTotalPct: z.number().nullable(),
  analyticsWeeklyMembershipsSoldToExpiredPct: z.number().nullable(),
  analyticsWeeklyTotalEnglishTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyTotalRussianTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyTotalSpanishTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyTotalFrenchTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyTotalGermanTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyTotalKoreanTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyTotalChineseSimplifiedTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyConfirmedEnglishTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyConfirmedRussianTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyConfirmedSpanishTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyConfirmedFrenchTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyConfirmedGermanTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyConfirmedKoreanTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyConfirmedChineseSimplifiedTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyUnconfirmedEnglishTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyUnconfirmedRussianTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyUnconfirmedSpanishTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyUnconfirmedFrenchTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyUnconfirmedGermanTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyUnconfirmedKoreanTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyUnconfirmedChineseSimplifiedTranslationsPhrases: z.number().nullable(),
  analyticsWeeklyConfirmedEnglishTranslationsPhrasesPct: z.number().nullable(),
  analyticsWeeklyConfirmedRussianTranslationsPhrasesPct: z.number().nullable(),
  analyticsWeeklyConfirmedSpanishTranslationsPhrasesPct: z.number().nullable(),
  analyticsWeeklyConfirmedFrenchTranslationsPhrasesPct: z.number().nullable(),
  analyticsWeeklyConfirmedGermanTranslationsPhrasesPct: z.number().nullable(),
  analyticsWeeklyConfirmedKoreanTranslationsPhrasesPct: z.number().nullable(),
  analyticsWeeklyConfirmedChineseSimplifiedTranslationsPhrasesPct: z.number().nullable(),
  analyticsWeeklyUnconfirmedEnglishTranslationsPhrasesPct: z.number().nullable(),
  analyticsWeeklyUnconfirmedRussianTranslationsPhrasesPct: z.number().nullable(),
  analyticsWeeklyUnconfirmedSpanishTranslationsPhrasesPct: z.number().nullable(),
  analyticsWeeklyUnconfirmedFrenchTranslationsPhrasesPct: z.number().nullable(),
  analyticsWeeklyUnconfirmedGermanTranslationsPhrasesPct: z.number().nullable(),
  analyticsWeeklyUnconfirmedKoreanTranslationsPhrasesPct: z.number().nullable(),
  analyticsWeeklyUnconfirmedChineseSimplifiedTranslationsPhrasesPct: z.number().nullable(),
  analyticsPastYearChineseSimplifiedEMUse: nullableNumberFromMissingSchema,
  analyticsPastYearEnglishEMUse: nullableNumberFromMissingSchema,
  analyticsPastYearFrenchEMUse: nullableNumberFromMissingSchema,
  analyticsPastYearGermanEMUse: nullableNumberFromMissingSchema,
  analyticsPastYearKoreanEMUse: nullableNumberFromMissingSchema,
  analyticsPastYearRussianEMUse: nullableNumberFromMissingSchema,
  analyticsPastYearSpanishEMUse: nullableNumberFromMissingSchema,
  analyticsWeeklyTotalChineseSimplifiedGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyTotalEnglishGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyTotalFrenchGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyTotalGermanGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyTotalKoreanGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyTotalRussianGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyTotalSpanishGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyConfirmedChineseSimplifiedGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyConfirmedEnglishGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyConfirmedFrenchGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyConfirmedGermanGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyConfirmedKoreanGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyConfirmedRussianGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyConfirmedSpanishGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyUnconfirmedChineseSimplifiedGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyUnconfirmedEnglishGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyUnconfirmedFrenchGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyUnconfirmedGermanGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyUnconfirmedKoreanGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyUnconfirmedRussianGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyUnconfirmedSpanishGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyConfirmedPercentageChineseSimplifiedGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyConfirmedPercentageEnglishGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyConfirmedPercentageFrenchGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyConfirmedPercentageGermanGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyConfirmedPercentageKoreanGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyConfirmedPercentageRussianGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyConfirmedPercentageSpanishGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyUnconfirmedPercentageChineseSimplifiedGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyUnconfirmedPercentageEnglishGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyUnconfirmedPercentageFrenchGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyUnconfirmedPercentageGermanGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyUnconfirmedPercentageKoreanGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyUnconfirmedPercentageRussianGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyUnconfirmedPercentageSpanishGlossaries: nullableNumberFromMissingSchema,
  analyticsWeeklyTotalProgramBreathworkEliteSold: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkEliteSoldAndroid: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkEliteSoldIOS: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkEliteSoldMove: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkEliteSoldShopify: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkIntensiveSold: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkIntensiveSoldAndroid: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkIntensiveSoldIOS: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkIntensiveSoldMove: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkIntensiveSoldShopify: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkIntesiveSold: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkIntesiveSoldAndroid: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkIntesiveSoldIOS: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkIntesiveSoldMove: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkIntesiveSoldShopify: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkIOS: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkLightPlusSold: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkLightPlusSoldAndroid: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkLightPlusSoldIOS: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkLightPlusSoldMove: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkLightPlusSoldShopify: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkLightSold: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkLightSoldAndroid: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkLightSoldIOS: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkLightSoldMove: z.number().nullable().optional(),
  analyticsWeeklyTotalProgramBreathworkLightSoldShopify: z.number().nullable().optional(),
  analyticsWeeklyTrainingProgramBreathworkEliteRevenue: z.number().nullable().optional(),
  analyticsWeeklyTrainingProgramBreathworkIntensiveRevenue: z.number().nullable().optional(),
  analyticsWeeklyTrainingProgramBreathworkIntensiveRevenueAndroid: z.number().nullable().optional(),
  analyticsWeeklyTrainingProgramBreathworkIntensiveRevenueIOS: z.number().nullable().optional(),
  analyticsWeeklyTrainingProgramBreathworkIntensiveRevenueMove: z.number().nullable().optional(),
  analyticsWeeklyTrainingProgramBreathworkIntensiveRevenueShopify: z.number().nullable().optional(),
  analyticsWeeklyTrainingProgramBreathworkLightPlusRevenue: z.number().nullable().optional(),
  analyticsWeeklyTrainingProgramBreathworkLightPlusRevenueAndroid: z.number().nullable().optional(),
  analyticsWeeklyTrainingProgramBreathworkEliteRevenueAndroid: z.number().nullable().optional(),
  analyticsWeeklyTrainingProgramBreathworkLightPlusRevenueIOS: z.number().nullable().optional(),
  analyticsWeeklyTrainingProgramBreathworkEliteRevenueIOS: z.number().nullable().optional(),
  analyticsWeeklyTrainingProgramBreathworkLightPlusRevenueMove: z.number().nullable().optional(),
  analyticsWeeklyTrainingProgramBreathworkEliteRevenueMove: z.number().nullable().optional(),
  analyticsWeeklyTrainingProgramBreathworkLightPlusRevenueShopify: z.number().nullable().optional(),
  analyticsWeeklyTrainingProgramBreathworkEliteRevenueShopify: z.number().nullable().optional(),
  analyticsWeeklyTrainingProgramBreathworkLightRevenue: z.number().nullable().optional(),
  analyticsWeeklyTrainingProgramBreathworkLightRevenueAndroid: z.number().nullable().optional(),
  analyticsWeeklyTrainingProgramBreathworkLightRevenueIOS: z.number().nullable().optional(),
  analyticsWeeklyTrainingProgramBreathworkLightRevenueMove: z.number().nullable().optional(),
  analyticsWeeklyTrainingProgramBreathworkLightRevenueShopify: z.number().nullable().optional(),
  analyticsWeeklyMainReport: nullableStringFromMissingSchema,
  analyticsWeeklyCjmBottleneckInsight: nullableStringFromMissingSchema,
  analyticsWeeklyInsights: nullableStringFromMissingSchema,
  historicalContent: historicalContentSchema.nullable().optional(),
  openaiWeeklyReportDiagnostics: openAIWeeklyReportDiagnosticsSummarySchema,
  dataCompleteness: z.enum(['unconfirmed', 'unknown']).optional(),
  dataQualityAvailability: z
    .enum(['recorded', 'unknown', 'api-unavailable', 'no-metrics'])
    .optional(),
  dataQualityIssueCount: z.number().int().nonnegative().optional(),
  dataQualityMetricKeys: z.array(z.string()).optional(),
  platformHealthPeriod: platformHealthPeriodSummarySchema,
});

const analyticsWeeklyLegacyDetailResponseSchema = z.object({
  item: analyticsWeeklyDetailItemSchema,
  previousWeekId: z.string().nullable(),
  nextWeekId: z.string().nullable(),
});

export const analyticsWeeklyDetailResponseSchema = z.preprocess((value) => {
  if (!isRecord(value) || !isRecord(value.data) || typeof value.data.id !== 'string') return value;
  const defaults = Object.fromEntries(
    Object.keys(analyticsWeeklyDetailItemSchema.shape).map((key) => [key, null])
  );
  // The legacy detail permits this field only when a quality source supplied it. A canonical
  // resource has no synthetic completeness label, so leave it absent rather than converting it
  // to an invalid null enum.
  delete defaults.dataCompleteness;
  delete defaults.dataQualityAvailability;
  delete defaults.dataQualityIssueCount;
  delete defaults.dataQualityMetricKeys;
  const metrics = isRecord(value.data.metrics) ? value.data.metrics : {};
  const currentContent = isRecord(value.data.content) ? value.data.content : null;
  const historicalResult = historicalContentSchema
    .nullable()
    .safeParse(value.data.historicalContent ?? null);
  if (!historicalResult.success) return value;
  const historicalContent = historicalResult.data;
  const hasQualityProjection = Object.hasOwn(metrics, 'dataQuality');
  const parsedQuality =
    hasQualityProjection && metrics.dataQuality !== null
      ? canonicalDataQualitySchema.safeParse(metrics.dataQuality)
      : null;
  if (parsedQuality && !parsedQuality.success) return value;
  const quality = parsedQuality?.data ?? null;
  const completeness = quality?.completeness;
  return {
    item: {
      ...defaults,
      ...canonicalReportToLegacyListItem(value.data),
      analyticsWeeklyMainReport:
        currentContent !== null && typeof currentContent.mainReport === 'string'
          ? currentContent.mainReport
          : (historicalContent?.mainReport ?? null),
      analyticsWeeklyCjmBottleneckInsight:
        currentContent !== null && typeof currentContent.bottleneckInsights === 'string'
          ? currentContent.bottleneckInsights
          : (historicalContent?.bottleneckInsights ?? null),
      analyticsWeeklyInsights:
        currentContent !== null && typeof currentContent.generalInsights === 'string'
          ? currentContent.generalInsights
          : (historicalContent?.generalInsights ?? null),
      historicalContent,
      dataCompleteness: completeness,
      dataQualityIssueCount: quality?.issues.length,
      dataQualityMetricKeys: quality?.issues
        .map((issue) => issue.metricKey)
        .filter((key): key is string => key !== null),
      platformHealthPeriod: metrics.platformHealthPeriod ?? null,
      dataQualityAvailability:
        value.data.metrics === null
          ? 'no-metrics'
          : hasQualityProjection
            ? quality === null
              ? 'unknown'
              : 'recorded'
            : 'api-unavailable',
    },
    previousWeekId: null,
    nextWeekId: null,
  };
}, analyticsWeeklyLegacyDetailResponseSchema);

export type AnalyticsWeeklyMetric = z.infer<typeof analyticsWeeklyMetricSchema>;
export type AnalyticsWeeklyReport = z.infer<typeof analyticsWeeklyListItemSchema>;
export type AnalyticsWeeklyDetailItem = z.infer<typeof analyticsWeeklyDetailItemSchema>;
export type AnalyticsWeeklyResponse = z.infer<typeof analyticsWeeklyResponseSchema>;
export type AnalyticsWeeklyDetailResponse = z.infer<typeof analyticsWeeklyDetailResponseSchema>;
